import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Where the top bar shows its back arrow instead of the breadcrumb trail. */
const PHONE_QUERY = '(max-width: 47.9375rem)';
/** A touch has to start this close to the leading edge to be a back swipe. */
const EDGE_PX = 24;
/** Movement below this is a tap that wobbled, not a gesture. */
const SLOP_PX = 8;
/** Released past this share of the width, the page goes back. */
const COMMIT_RATIO = 0.35;
/** A shorter, fast flick goes back too (px per ms over the last moments). */
const FLICK_SPEED = 0.5;
const FLICK_MIN_PX = 40;
const FLICK_WINDOW_MS = 100;
/** If the next page never arrives, put this one back rather than leave a blank. */
const RESTORE_AFTER_MS = 3000;

/**
 * True in an app installed to the iPhone home screen. Safari in a tab has its
 * own edge swipe and Android has the system back gesture; only the installed
 * iOS app has no way back short of the arrow in the top bar.
 */
const isInstalledIos = () =>
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * Swipe in from the leading edge to go back, as on any iOS screen.
 *
 * The page follows the finger; released far enough or flicked, it slides off
 * and the previous page comes back (the route motion slides it in from the
 * start). Back means the previous screen when there is one in this session,
 * as the system gesture does, and otherwise the page's parent, as the back
 * arrow does. Only on pages that have a parent, at the width where the top
 * bar shows the arrow, and only once the gesture is clearly sideways, so a
 * scroll that starts near the edge still scrolls.
 */
export function useEdgeSwipeBack(
  parentHref: string | undefined,
  isRTL: boolean,
) {
  const navigate = useNavigate();
  const location = useLocation();
  const target = useRef(parentHref);
  useEffect(() => {
    target.current = parentHref;
  });
  /** The content element left off screen by a committed swipe. */
  const parked = useRef<{ el: HTMLElement; timer: number } | null>(null);

  // The next page is in: clear what the swipe left on the old content element
  // (the shell keeps it), under the route motion's slide-in.
  useLayoutEffect(() => {
    const p = parked.current;
    if (!p) return;
    parked.current = null;
    window.clearTimeout(p.timer);
    clearStyle(p.el);
  }, [location.key]);

  const enabled = !!parentHref;
  useEffect(() => {
    if (!enabled || !isInstalledIos()) return;
    const main = document.getElementById('main-content');
    if (!main) return;
    const dir = isRTL ? -1 : 1;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let dragging = false;
    let distance = 0;
    let samples: Array<[number, number]> = [];
    let content: HTMLElement | null = null;

    const onStart = (e: TouchEvent) => {
      tracking = false;
      if (
        e.touches.length !== 1 ||
        parked.current ||
        !window.matchMedia(PHONE_QUERY).matches
      ) {
        return;
      }
      const x = e.touches[0].clientX;
      const fromEdge = isRTL ? window.innerWidth - x : x;
      if (fromEdge > EDGE_PX) return;
      content = main.firstElementChild as HTMLElement | null;
      if (!content) return;
      tracking = true;
      dragging = false;
      distance = 0;
      startX = x;
      startY = e.touches[0].clientY;
      // From the touch, not the first move: a flick can be over in one move.
      samples = [[e.timeStamp, 0]];
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking || !content) return;
      const dx = (e.touches[0].clientX - startX) * dir;
      const dy = e.touches[0].clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
        if (dx <= 0 || Math.abs(dx) < Math.abs(dy) * 1.5) {
          tracking = false;
          return;
        }
        dragging = true;
        content.style.transition = 'none';
      }
      // The page moves sideways now, not the scroller under it.
      if (e.cancelable) e.preventDefault();
      distance = Math.max(0, dx);
      const now = e.timeStamp;
      samples.push([now, distance]);
      while (samples.length > 2 && now - samples[0][0] > FLICK_WINDOW_MS) {
        samples.shift();
      }
      const fade = 1 - (distance / window.innerWidth) * 0.4;
      content.style.transform = `translate3d(${distance * dir}px, 0, 0)`;
      content.style.opacity = String(fade);
    };

    const onEnd = (e: TouchEvent) => {
      if (!tracking || !dragging || !content) {
        tracking = false;
        return;
      }
      tracking = false;
      dragging = false;
      const el = content;
      const [fromAt, fromDistance] = samples[0];
      const speed =
        (distance - fromDistance) / Math.max(1, e.timeStamp - fromAt);
      const commit =
        distance > window.innerWidth * COMMIT_RATIO ||
        (distance > FLICK_MIN_PX && speed > FLICK_SPEED);

      if (!commit || !target.current) {
        el.style.transition =
          'transform 200ms ease-out, opacity 200ms ease-out';
        el.style.transform = '';
        el.style.opacity = '';
        return;
      }

      el.style.transition = 'transform 160ms ease-in, opacity 160ms ease-in';
      el.style.transform = `translate3d(${window.innerWidth * dir}px, 0, 0)`;
      el.style.opacity = '0';
      parked.current = {
        el,
        timer: window.setTimeout(() => {
          parked.current = null;
          clearStyle(el);
        }, RESTORE_AFTER_MS),
      };
      // React Router numbers its entries: above 0 there is an in-app page
      // to return to, so this is the system's "back", scroll position and all.
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      const href = target.current;
      window.setTimeout(() => {
        if (idx > 0) navigate(-1);
        else navigate(href);
      }, 140);
    };

    main.addEventListener('touchstart', onStart, { passive: true });
    // Not passive: once the swipe is sideways it has to stop the scroll.
    main.addEventListener('touchmove', onMove, { passive: false });
    main.addEventListener('touchend', onEnd, { passive: true });
    main.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      main.removeEventListener('touchstart', onStart);
      main.removeEventListener('touchmove', onMove);
      main.removeEventListener('touchend', onEnd);
      main.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, isRTL, navigate]);
}

function clearStyle(el: HTMLElement) {
  el.style.transition = '';
  el.style.transform = '';
  el.style.opacity = '';
}
